import { computerConnectionProgressObservationSchema, computerConnectionProgressPhaseSchema } from "./computer-connection-progress.js";
import { createPrepareSessionRequestSchema, prepareSessionSnapshotSchema } from "./schemas/prepare/session.js";
import * as z$1 from "zod/v4";
//#region src/computer-connection.ts
const computerConnectionIntentSchema = z$1.strictObject({
	expectedAccount: z$1.string().trim().min(1).max(256),
	previousSessionIdentity: z$1.string().min(1).max(128)
});
const computerSetupVerboseOutputSchema = z$1.object({
	type: z$1.string(),
	code: z$1.string().min(1).max(256),
	text: z$1.string().optional(),
	message: z$1.string().optional(),
	data: z$1.object({
		state: z$1.string().optional(),
		phase: computerConnectionProgressPhaseSchema.optional()
	}).optional()
});
const computerSetupOutputSchema = z$1.union([computerSetupVerboseOutputSchema, z$1.object({
	t: z$1.string(),
	c: z$1.string().min(1).max(256),
	x: z$1.string().optional(),
	m: z$1.string().optional(),
	d: computerSetupVerboseOutputSchema.shape.data
}).transform((event) => ({
	type: event.t,
	code: event.c,
	text: event.x,
	message: event.m,
	data: event.d
}))]);
const desktopComputerConnectionProgressSchema = computerConnectionProgressObservationSchema.extend({ expectedAccount: computerConnectionIntentSchema.shape.expectedAccount });
const computerConnectionReviewSchema = z$1.strictObject({
	protocol: z$1.literal("computer-connection-v1"),
	previousSessionIdentity: z$1.string().min(1).max(128),
	account: z$1.strictObject({
		id: z$1.string().min(1).max(256),
		email: z$1.string().max(512).nullable()
	}).nullable(),
	existingConnection: z$1.strictObject({
		deviceId: z$1.string().min(1),
		runtimeLane: z$1.enum([
			"prod",
			"dev-local",
			"canary"
		])
	}).nullable()
});
const desktopComputerDisconnectRequestSchema = z$1.strictObject({
	expectedAccount: computerConnectionIntentSchema.shape.expectedAccount,
	expectedConnection: computerConnectionReviewSchema.shape.existingConnection.unwrap().optional()
});
const desktopComputerConnectionRequestSchema = createPrepareSessionRequestSchema.pick({
	humanProfileId: true,
	targetSpaceId: true,
	sourceNavigationAction: true,
	connectedComputerId: true
}).extend({
	expectedAccount: computerConnectionIntentSchema.shape.expectedAccount,
	resetThisComputer: z$1.boolean().optional()
});
const desktopComputerConnectionResultSchema = z$1.discriminatedUnion("status", [
	z$1.strictObject({
		status: z$1.literal("failed"),
		message: z$1.string().min(1),
		diagnosticId: z$1.string().min(1),
		diagnosticsPath: z$1.string().min(1).optional()
	}),
	z$1.strictObject({
		status: z$1.literal("reused"),
		connection: computerConnectionReviewSchema.shape.existingConnection.unwrap()
	}),
	z$1.strictObject({
		status: z$1.literal("completed"),
		snapshot: prepareSessionSnapshotSchema
	}),
	z$1.strictObject({ status: z$1.literal("cancelled") })
]);
const desktopComputerConnectionOperationSchema = z$1.strictObject({
	operationId: z$1.string().min(1).max(128),
	request: desktopComputerConnectionRequestSchema,
	status: z$1.enum(["pending", "settled"]),
	progress: desktopComputerConnectionProgressSchema.nullable(),
	result: desktopComputerConnectionResultSchema.nullable(),
	errorMessage: z$1.string().min(1).max(4096).nullable()
});
const desktopComputerDisconnectPhaseSchema = z$1.enum([
	"checking",
	"waiting_for_confirmation",
	"disconnecting"
]);
const desktopComputerDisconnectResultSchema = z$1.union([
	z$1.strictObject({ status: z$1.literal("disconnected") }),
	z$1.strictObject({ status: z$1.literal("cancelled") }),
	z$1.strictObject({
		status: z$1.enum([
			"connection_changed",
			"failed",
			"unconfirmed"
		]),
		code: z$1.string().min(1).max(256),
		message: z$1.string().min(1).max(4096),
		diagnosticId: z$1.string().min(1).max(128).optional(),
		diagnosticsPath: z$1.string().min(1).max(4096).optional()
	})
]);
const desktopComputerDisconnectOperationSchema = z$1.strictObject({
	kind: z$1.literal("disconnect"),
	operationId: desktopComputerConnectionOperationSchema.shape.operationId,
	request: desktopComputerDisconnectRequestSchema,
	status: z$1.enum(["pending", "settled"]),
	phase: desktopComputerDisconnectPhaseSchema,
	result: desktopComputerDisconnectResultSchema.nullable()
});
const desktopComputerOperationSchema = z$1.union([desktopComputerConnectionOperationSchema, desktopComputerDisconnectOperationSchema]);
//#endregion
export { computerConnectionIntentSchema, computerConnectionReviewSchema, computerSetupOutputSchema, desktopComputerConnectionOperationSchema, desktopComputerConnectionProgressSchema, desktopComputerConnectionRequestSchema, desktopComputerConnectionResultSchema, desktopComputerDisconnectOperationSchema, desktopComputerDisconnectPhaseSchema, desktopComputerDisconnectRequestSchema, desktopComputerDisconnectResultSchema, desktopComputerOperationSchema };

//# sourceMappingURL=computer-connection.js.map